import { Play, PuzzlePiece, PencilLine, GameController, BookOpenText, Leaf, FlowerLotus, Palette, GridFour, Clock } from "@phosphor-icons/react";

// Landing sections: the four learning cards and the non-interactive Calm & Play preview.
const CARDS = [
  { id: "wordplay", title: "Word Play", desc: "Listen, match, trace, and explore.", bg: "bg-[#e4f6f3] border-[#c9ebe6]", tile: "bg-[#c9ebe6]", icon: PuzzlePiece, tint: "text-[#15a6a0]", href: "/kids-vocab" },
  { id: "sentence", title: "Sentence Builder", desc: "Put words together. Make your ideas shine.", bg: "bg-[#efe7fb] border-[#ded0f5]", tile: "bg-[#ded0f5]", icon: PencilLine, tint: "text-[#6a3fc7]" },
  { id: "wordgame", title: "Word Games", desc: "Discover meanings, opposites, and stronger words.", bg: "bg-[#fff5dc] border-[#f6e4ad]", tile: "bg-[#f9e9b8]", icon: GameController, tint: "text-[#8b5cf6]" },
  { id: "storycreator", title: "Story Magic", desc: "Turn your ideas into a story.", bg: "bg-[#fde9f1] border-[#f6cfdd]", tile: "bg-[#f8d6e3]", icon: BookOpenText, tint: "text-[#d9488a]" },
];

export const KidsActivityCards = ({ onOpen }) => (
  <section className="mt-8" id="kids-activities" aria-labelledby="kids-activities-title" data-testid="kids-activities">
    <h2 id="kids-activities-title" className="font-display font-extrabold text-3xl sm:text-4xl text-[#3d2378] text-center mb-5">
      <span aria-hidden="true" className="text-[#f2b544] text-xl mr-3">✦</span>What would you like to do?<span aria-hidden="true" className="text-[#f48fb1] text-xl ml-3">✦</span>
    </h2>
    <div className="grid md:grid-cols-2 gap-5">
      {CARDS.map((c) => {
        const inner = (
          <>
            <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-3xl ${c.tile} flex items-center justify-center shrink-0`}>
              <c.icon size={56} weight="duotone" className={c.tint} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display font-bold text-2xl text-[#3d2378]">{c.title}</h3>
              <p className="font-nunito text-base text-[#5b5572] mt-1 leading-snug">{c.desc}</p>
            </div>
            <span className="w-12 h-12 rounded-full bg-[#15a6a0] text-white flex items-center justify-center shrink-0 shadow-[0_8px_20px_rgba(21,166,160,0.35)] group-hover:bg-[#108f8a] transition-colors" aria-hidden="true">
              <Play size={20} weight="fill" className="ml-0.5" />
            </span>
          </>
        );
        const cls = `group w-full text-left flex items-center gap-5 p-5 sm:p-6 rounded-[1.75rem] border ${c.bg} hover:-translate-y-0.5 transition-transform focus-visible:ring-4 focus-visible:ring-[#b79cf0]`;
        return c.href ? (
          <a key={c.id} href={c.href} className={cls} data-testid={`activity-card-${c.id}`} aria-label={`${c.title}: ${c.desc}`}>{inner}</a>
        ) : (
          <button key={c.id} type="button" onClick={() => onOpen(c.id)} className={cls} data-testid={`activity-card-${c.id}`} aria-label={`${c.title}: ${c.desc}`}>{inner}</button>
        );
      })}
    </div>
  </section>
);

const CALM = [
  { id: "fingers", label: "Finger Moves with Lumi", icon: FlowerLotus, tint: "text-[#8b5cf6]" },
  { label: "Draw & Create", icon: Palette, tint: "text-[#e8740f]" },
  { label: "Tic-Tac-Toe", icon: GridFour, tint: "text-[#15a6a0]" },
];

export const KidsCalmPlay = ({ onOpen }) => (
  <section className="mt-6 rounded-[1.75rem] bg-[#e7f7f2] border border-[#c9ebe6] p-5 sm:p-6 grid lg:grid-cols-5 gap-5 items-center" aria-labelledby="calm-play-title" data-testid="kids-calm-play">
    <div className="lg:col-span-2 flex items-start gap-4">
      <span className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shrink-0"><Leaf size={28} weight="fill" className="text-[#15a6a0]" /></span>
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="calm-play-title" className="font-display font-extrabold text-2xl sm:text-3xl text-[#3d2378]">Lumi’s Calm &amp; Play <span aria-hidden="true" className="text-[#f2b544] text-lg">✦</span></h2>
          <span className="inline-flex items-center gap-1.5 bg-white text-[#15a6a0] font-nunito font-bold text-xs rounded-full px-3 py-1.5" data-testid="calm-play-coming-soon"><Clock size={14} weight="bold" /> More coming soon</span>
        </div>
        <p className="font-nunito text-base text-[#5b5572] mt-1">Choose a quiet moment or a gentle game.</p>
      </div>
    </div>
    <ul className="lg:col-span-3 grid grid-cols-3 gap-3" aria-label="Calm and play activities">
      {CALM.map((c) => (
        <li key={c.label} className="flex">
          {c.id ? (
            <button
              type="button"
              onClick={() => onOpen(c.id)}
              className="group relative w-full bg-white border-2 border-[#15a6a0] rounded-2xl p-4 flex flex-col items-center text-center gap-2 hover:-translate-y-0.5 transition-transform focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
              data-testid={`calm-tile-${c.id}`}
              aria-label={`${c.label}: open activity`}
            >
              <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 bg-[#15a6a0] text-white font-nunito font-extrabold text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5" aria-hidden="true"><Play size={10} weight="fill" /> Play</span>
              <c.icon size={34} weight="duotone" className={c.tint} />
              <span className="font-nunito font-bold text-xs sm:text-sm text-[#4a2e8a] leading-tight">{c.label}</span>
            </button>
          ) : (
            <div className="w-full bg-white/80 border border-[#c9ebe6] rounded-2xl p-4 flex flex-col items-center text-center gap-2 select-none" aria-disabled="true" data-testid={`calm-tile-soon-${c.label.replace(/\W+/g, "-").toLowerCase()}`}>
              <c.icon size={34} weight="duotone" className={c.tint} />
              <span className="font-nunito font-bold text-xs sm:text-sm text-[#4a2e8a] leading-tight">{c.label}</span>
            </div>
          )}
        </li>
      ))}
    </ul>
  </section>
);
